/*
 * Input (Figma 119:45192 / 119:44928) and Textarea (119:45215).
 * Wrap: column, gap 8. Label: 550 14/20 --text-default, ss03.
 * Input field: radius 8, --surface-raised fill, padding 2px 8px (left 8px from Figma; right 8px
 * assumed), inner text box padding 6px 0 → 36px tall; 1px --border-strong inside stroke over a 2px
 * 50%-white inner highlight (--field-stroke). Text 400 14/20 ss03+ss04; placeholder --text-weak.
 * Textarea field: height 120, padding 8px, same stroke/fill/text.
 * Not in the design: focused state → uses "Button Focus" (--focus-field), the design's
 * active-field ring; error state → --text-danger message under the field; on a touch screen
 * (pointer: coarse) the input is 40px high with 16px text (smaller text makes iOS zoom in on focus).
 */
import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { cx } from './cx'

const FIELD =
    'w-full rounded-[8px] bg-[var(--surface-raised)] shadow-[var(--field-stroke)] focus-within:shadow-[var(--field-stroke),var(--focus-field)]'
const TEXT =
    'ss03-ss04 w-full bg-transparent text-[14px] leading-[20px] font-normal text-[var(--text-default)] placeholder:text-[var(--text-weak)] outline-none ' +
    // Touch: 16px text, or iOS zooms the page in on focus.
    'pointer-coarse:text-[length:var(--font-size-medium)]'

function FieldShell({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
    return (
        <div className="flex w-full flex-col items-start gap-[8px]">
            <label htmlFor={id} className="ss03 flex items-center whitespace-nowrap text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">
                {label}
            </label>
            {children}
            {error && (
                <p id={`${id}-error`} role="alert" className="text-[14px] leading-[20px] font-normal text-[var(--text-danger)]">
                    {error}
                </p>
            )}
        </div>
    )
}

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
    label: string
    error?: string
}

export function TextInput({ label, error, className, ...rest }: TextInputProps) {
    const id = useId()
    return (
        <FieldShell id={id} label={label} error={error}>
            <div className={cx(FIELD, 'px-[8px] py-[2px]', className)}>
                <input
                    id={id}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    className={cx(TEXT, 'block h-[32px] py-[6px] pointer-coarse:h-[36px]')}
                    {...rest}
                />
            </div>
        </FieldShell>
    )
}

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
    label: string
    error?: string
}

export function Textarea({ label, error, className, ...rest }: TextareaProps) {
    const id = useId()
    return (
        <FieldShell id={id} label={label} error={error}>
            <div className={cx(FIELD, 'h-[120px] p-[8px]', className)}>
                <textarea id={id} aria-invalid={error ? true : undefined} className={cx(TEXT, 'h-full resize-none')} {...rest} />
            </div>
        </FieldShell>
    )
}
